/*
  INFA COMPARE
  Native product compare, no app. The list of product handles lives in localStorage ("infa-compare"), capped by the
  theme setting. Three custom elements:
    <infa-compare-toggle handle="quattro-onyx">   a checkbox-style button on product pages and cards
    <infa-compare-bar>                            the sticky bar at the bottom of the page (snippets/infa-compare-bar.liquid)
    <infa-compare-table>                          the compare page (sections/infa-compare.liquid); fetches each product's
                                                  compare column from /products/<handle>?view=compare-card and lays
                                                  the columns out as rows
  Everything reads its texts from data attributes so the wording stays editable in the theme editor.
*/
(function () {
  const KEY = 'infa-compare';
  const EVENT = 'infa:compare';
  const cfg = window.InfaCompareConfig || {};
  const MAX = Math.max(2, Math.min(6, parseInt(cfg.max, 10) || 4));

  function read() {
    try {
      const v = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(v) ? v.filter((h) => typeof h === 'string' && h).slice(0, MAX) : [];
    } catch (e) { return []; }
  }
  function write(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX))); } catch (e) { /* private mode */ }
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { list: read() } }));
  }
  const store = {
    list: read,
    has: (h) => read().includes(h),
    add(h) { const l = read(); if (l.includes(h)) return true; if (l.length >= MAX) return false; l.push(h); write(l); return true; },
    remove(h) { write(read().filter((x) => x !== h)); },
    toggle(h) { return this.has(h) ? (this.remove(h), false) : this.add(h); },
    clear() { write([]); },
    max: MAX
  };
  window.InfaCompare = store;

  /* ---------- toggle ---------- */
  class CompareToggle extends HTMLElement {
    connectedCallback() {
      this.handle = this.getAttribute('handle');
      this.button = this.querySelector('button') || this;
      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        const ok = store.toggle(this.handle);
        if (!ok) this.flashLimit();
      });
      this.sync();
      document.addEventListener(EVENT, () => this.sync());
    }
    sync() {
      const on = store.has(this.handle);
      this.classList.toggle('is-active', on);
      this.button.setAttribute('aria-pressed', on ? 'true' : 'false');
      const label = this.querySelector('[data-label]');
      if (label) label.textContent = on ? (this.dataset.textOn || label.textContent) : (this.dataset.textOff || label.textContent);
    }
    flashLimit() {
      const msg = this.dataset.textLimit || `You can compare up to ${MAX} products`;
      let tip = this.querySelector('.infa-compare-toggle__tip');
      if (!tip) { tip = document.createElement('span'); tip.className = 'infa-compare-toggle__tip'; this.appendChild(tip); }
      tip.textContent = msg;
      this.classList.add('is-limit');
      clearTimeout(this._t);
      this._t = setTimeout(() => this.classList.remove('is-limit'), 2500);
    }
  }

  /* ---------- sticky bar ---------- */
  class CompareBar extends HTMLElement {
    connectedCallback() {
      this.count = this.querySelector('[data-count]');
      this.items = this.querySelector('[data-items]');
      this.clearBtn = this.querySelector('[data-clear]');
      this.link = this.querySelector('[data-link]');
      if (this.clearBtn) this.clearBtn.addEventListener('click', () => store.clear());
      this.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-remove]');
        if (rm) store.remove(rm.dataset.remove);
      });
      this.sync();
      document.addEventListener(EVENT, () => this.sync());
    }
    sync() {
      const list = store.list();
      const onComparePage = document.querySelector('infa-compare-table');
      this.hidden = list.length === 0 || !!onComparePage;
      document.documentElement.classList.toggle('infa-compare-bar-open', !this.hidden);
      if (this.count) this.count.textContent = list.length;
      if (this.link) this.link.classList.toggle('is-disabled', list.length < 2);
      if (this.items) {
        this.items.innerHTML = list.map((h) => {
          const meta = this.lookup(h);
          return `<span class="infa-compare-bar__item">${meta.image ? `<img src="${meta.image}" alt="" width="32" height="32" loading="lazy">` : ''}<span>${meta.title}</span><button type="button" data-remove="${h}" aria-label="Remove ${meta.title}">&times;</button></span>`;
        }).join('');
      }
      // Remember titles/images we have seen so the bar can label items on pages where the product is not present
      list.forEach((h) => this.lookup(h));
    }
    lookup(handle) {
      let cache = {};
      try { cache = JSON.parse(sessionStorage.getItem(KEY + '-meta') || '{}'); } catch (e) {}
      const el = document.querySelector(`infa-compare-toggle[handle="${handle}"]`);
      if (el && el.dataset.title) {
        cache[handle] = { title: el.dataset.title, image: el.dataset.image || '' };
        try { sessionStorage.setItem(KEY + '-meta', JSON.stringify(cache)); } catch (e) {}
      }
      return cache[handle] || { title: handle.replace(/-/g, ' '), image: '' };
    }
  }

  /* ---------- compare page ---------- */
  class CompareTable extends HTMLElement {
    connectedCallback() {
      // Children may not be parsed yet when the element is upgraded early
      if (!this.querySelector('[data-body]')) {
        if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true }); return; }
        requestAnimationFrame(() => this.connectedCallback()); return;
      }
      if (this.__init) return; this.__init = true;
      this.endpoint = this.dataset.endpoint || '?view=compare-card';
      this.labels = JSON.parse(this.dataset.labels || '{}');
      this.rows = (this.dataset.rows || '').split(',').map((s) => s.trim()).filter(Boolean);
      this.defaults = (this.dataset.defaults || '').split(',').map((s) => s.trim()).filter(Boolean);
      this.body = this.querySelector('[data-body]');
      this.empty = this.querySelector('[data-empty]');
      this.status = this.querySelector('[data-status]');
      this.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-remove]');
        if (rm) { store.remove(rm.dataset.remove); }
        const clear = e.target.closest('[data-clear]');
        if (clear) { store.clear(); }
      });
      document.addEventListener(EVENT, () => this.render());
      this.render();
    }
    async render() {
      let list = store.list();
      const usingDefaults = list.length === 0 && this.defaults.length > 0;
      if (usingDefaults) list = this.defaults.slice(0, MAX);
      this.classList.toggle('is-defaults', usingDefaults);
      if (this.status) this.status.hidden = !usingDefaults;
      if (list.length === 0) { this.body.innerHTML = ''; if (this.empty) this.empty.hidden = false; return; }
      if (this.empty) this.empty.hidden = true;
      this.body.setAttribute('aria-busy', 'true');
      const cols = await Promise.all(list.map((h) => this.fetchColumn(h)));
      this.body.removeAttribute('aria-busy');
      const live = cols.filter(Boolean);
      // Drop handles that no longer exist
      list.forEach((h, i) => { if (!cols[i] && !usingDefaults) store.remove(h); });
      this.body.innerHTML = this.build(live, usingDefaults);
      this.style.setProperty('--infa-compare-cols', live.length);
    }
    async fetchColumn(handle) {
      try {
        const res = await fetch(`/products/${handle}${this.endpoint}`, { headers: { Accept: 'text/html' } });
        if (!res.ok) return null;
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const col = doc.querySelector('[data-compare-col]');
        return col ? col : null;
      } catch (e) { return null; }
    }
    build(cols, usingDefaults) {
      const cell = (col, row) => { const c = col.querySelector(`[data-row="${row}"]`); return c ? c.innerHTML : '<span class="infa-compare__na">&ndash;</span>'; };
      let out = '<div class="infa-compare__row infa-compare__row--head"><div class="infa-compare__label"></div>';
      cols.forEach((col) => {
        const h = col.dataset.handle;
        out += `<div class="infa-compare__cell infa-compare__cell--head">${usingDefaults ? '' : `<button type="button" class="infa-compare__remove" data-remove="${h}" aria-label="${this.labels.remove || 'Remove'}">&times;</button>`}${cell(col, 'head')}</div>`;
      });
      out += '</div>';
      this.rows.forEach((row) => {
        // Skip a row when every product is empty for it
        const any = cols.some((col) => { const c = col.querySelector(`[data-row="${row}"]`); return c && c.textContent.trim() !== ''; });
        if (!any) return;
        const label = Object.prototype.hasOwnProperty.call(this.labels, row) ? this.labels[row] : row.charAt(0).toUpperCase() + row.slice(1);
        out += `<div class="infa-compare__row" data-row="${row}"><div class="infa-compare__label">${label}</div>`;
        cols.forEach((col) => { out += `<div class="infa-compare__cell">${cell(col, row)}</div>`; });
        out += '</div>';
      });
      return out;
    }
  }

  if (!customElements.get('infa-compare-toggle')) customElements.define('infa-compare-toggle', CompareToggle);
  if (!customElements.get('infa-compare-bar')) customElements.define('infa-compare-bar', CompareBar);
  if (!customElements.get('infa-compare-table')) customElements.define('infa-compare-table', CompareTable);
})();
